import {inject, Injectable} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {injectMutation, QueryClient} from '@tanstack/angular-query-experimental';
import {AiService, ResolveAiNameRequest, SendAiMessageRequest, SendAiMessageResponse} from '../../../../api';
import {QueryKey} from '../../../constants/query-key';

export interface SendAiMessageInput {
  conversationId: number;
  body: SendAiMessageRequest;
}

export interface ResolveAiNameInput {
  conversationId: number;
  body: ResolveAiNameRequest;
}

@Injectable()
export class AiChatService {

  private readonly aiService = inject(AiService);
  private readonly queryClient = inject(QueryClient);

  startConversationMutation = injectMutation(() => ({
    mutationFn: () => lastValueFrom(this.aiService.startAiConversation()),
  }));

  sendMessageMutation = injectMutation(() => ({
    mutationFn: (input: SendAiMessageInput): Promise<SendAiMessageResponse> =>
      lastValueFrom(this.aiService.sendAiMessage(input.conversationId, input.body)),
  }));

  resolveNameMutation = injectMutation(() => ({
    mutationFn: (input: ResolveAiNameInput) =>
      lastValueFrom(this.aiService.resolveAiName(input.conversationId, input.body)),
  }));

  // Une action confirmée crée ou modifie un todo : la liste doit se rafraîchir sans rechargement de page.
  confirmActionMutation = injectMutation(() => ({
    mutationFn: (messageId: number) => lastValueFrom(this.aiService.confirmAiAction(messageId)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODOS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO_STATUS_SUMMARIES]});
    },
  }));

  cancelActionMutation = injectMutation(() => ({
    mutationFn: (messageId: number) => lastValueFrom(this.aiService.cancelAiAction(messageId)),
  }));

  transcribeMutation = injectMutation(() => ({
    mutationFn: (file: File) => lastValueFrom(this.aiService.transcribeAudio(file)),
  }));

  /**
   * Jette la conversation à la fermeture du panneau : elle ne servait qu'aux allers-retours.
   * Appelée pendant la destruction du composant, donc en abonnement nu plutôt qu'en mutation —
   * l'injecteur du panneau n'existe déjà plus, alors que HttpClient, lui, est global.
   * L'échec n'est pas remonté : il n'y a plus d'écran pour l'afficher.
   */
  discardConversation(conversationId: number): void {
    this.aiService.deleteAiConversation(conversationId).subscribe({error: () => undefined});
  }
}
